/**
 * What an edit-bookmark window shows, and everything it can do to the bookmark.
 *
 * One backing view model behind every platform's editor, for §05 of
 * docs/kmp-sharing-architecture.html's reason and for a sharper one that belongs to this
 * surface in particular: **an editor has more states than it looks like**, and each of
 * them is a decision rather than a rendering detail. Loading, ready, saving over a form
 * that must stay on screen, refused with a sentence from the server, and *gone* — the
 * bookmark deleted underneath the window while it was open. Getting the last of those
 * wrong on one platform means a Save that silently recreates a row the user deliberately
 * deleted, which is the kind of bug that is discovered a month later by somebody wondering
 * why a link keeps coming back.
 *
 * ── The window's bookmark never changes ──────────────────────────────────────
 *
 * The id is a constructor parameter, not something an intent can repoint. That is what
 * makes `WindowBodies.contentFor`'s cache — a body built once per window id, with the
 * content ignored afterwards — still correct now that a window kind has an argument; its
 * own comment named this ticket as the one that would have to check. Opening a different
 * bookmark opens a different window, and [WorkspaceBackingViewModel.onEditBookmarkActivated]
 * is where "the same bookmark twice" becomes a focus rather than a second window.
 *
 * ── What this model does *not* own ───────────────────────────────────────────
 *
 * The URL. `PUT /api/bookmarks/{id}` can move a bookmark to a new one — the id is the
 * identity there, so it is a move rather than a duplicate — and this window still refuses
 * to. That is a UI decision rather than a server limit: the URL is what a bookmark *is*,
 * and a text field holding it invites a typo whose effect is a bookmark that silently no
 * longer points anywhere. So [State.Ready.url] is shown, copied, and sent back verbatim
 * on every save.
 *
 * Also not owned: [se.soderbjorn.lunapin.clientserver.Bookmark.aiSummary] and
 * [se.soderbjorn.lunapin.clientserver.Bookmark.aiState], which no request carries at all.
 *
 * @see BookmarkListBackingViewModel
 */
package se.soderbjorn.lunapin.client.viewmodel

import io.ktor.http.HttpStatusCode
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.launch
import kotlin.coroutines.cancellation.CancellationException
import se.soderbjorn.lunapin.client.StorageRepository
import se.soderbjorn.lunapin.client.readableMessage
import se.soderbjorn.lunapin.clientserver.ApiFailure
import se.soderbjorn.lunapin.clientserver.Bookmark
import se.soderbjorn.lunapin.clientserver.EditBookmarkRequest
import se.soderbjorn.lunapin.clientserver.TagUse

/**
 * Backs one edit-bookmark window.
 *
 * @param bookmarkId the row this window edits, for its whole life. See the file header.
 * @param storage the session's source of truth and the only route to the server.
 * @param scope where a save and a delete are launched. Injected with a default for
 *   [WorkspaceBackingViewModel]'s reason — a test supplies its own and can await it —
 *   and it is deliberately *not* the scope [run] is collected on: a save the user has
 *   asked for should finish even if they close the window a moment later, whereas a load
 *   nobody is looking at any more should not.
 */
class EditBookmarkBackingViewModel(
    private val bookmarkId: Long,
    private val storage: StorageRepository,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main),
) {
    private val _stateFlow = MutableStateFlow(State(loading = State.Loading))

    /** The complete, render-ready description of the window. */
    val stateFlow = _stateFlow.asStateFlow()

    /**
     * Every reason to fetch, counted — [BookmarkListBackingViewModel]'s mechanism, for its
     * reason: a tick rather than a `Unit`, because a `StateFlow` conflates equal values
     * and two retries in a row must be two fetches.
     */
    private val reloads = MutableStateFlow(0)

    /**
     * Load the bookmark, then reload it whenever asked. Called once by the platform;
     * never returns.
     */
    @Throws(CancellationException::class, Exception::class)
    suspend fun run(): Unit = coroutineScope {
        launch { watchForChangesElsewhere() }
        // Collecting a StateFlow delivers its current value first, so the initial load is
        // this and not a separate call. It never completes, which keeps `coroutineScope` open.
        reloads.collect { load() }
    }

    /**
     * Notice when *this* bookmark changes somewhere else — LPN-27's channel, arriving here.
     *
     * **The ids are the point, and this is the surface they exist for.** A list re-reads on any
     * change; an editor must not, because a save on an unrelated bookmark is not a reason to
     * disturb a form somebody is typing into. `BookmarksChange.covers` is the comparison, and
     * it deliberately answers true for an unenumerated change — an import — since "not listed"
     * there means "not enumerated", not "not affected".
     *
     * What happens next is [onChangedElsewhere]'s decision, and it is not simply a reload: see
     * that function, where the three branches are argued.
     *
     * The first value is skipped, for [reloadOnChangesElsewhere]'s reason in the list model:
     * `revision = 0` is "nothing has been said yet", and treating it as a change would mean
     * every editor opening with two fetches.
     */
    private suspend fun watchForChangesElsewhere() {
        storage.bookmarksChangedFlow.collect { change ->
            if (change.revision == 0 || !change.covers(bookmarkId)) return@collect
            onChangedElsewhere()
        }
    }

    /**
     * Ask again — the retry on the failure panel, and the way out of a load that lost the
     * network.
     *
     * Deliberately reachable from *[State.Gone]* too, so that the one state with no form
     * in it is not a dead end: a bookmark can come back, in the sense that the failure
     * that produced this state can have been a bad answer rather than a deletion.
     */
    fun onReloadRequested() {
        _stateFlow.value = State(loading = State.Loading)
        reloads.value += 1
    }

    /**
     * The server says this bookmark changed somewhere else — LPN-27's channel naming this id.
     *
     * **The one case where re-reading is not obviously right**, and the reason this is a
     * separate intent rather than a call to [onReloadRequested]. That one flashes the window
     * back to [State.Loading] and refetches, which is exactly what a person pressing *Try
     * again* asked for and exactly what somebody mid-sentence in the note field did not.
     *
     * So there are three answers, decided here rather than at each surface:
     *
     *  - **Nothing typed.** Reload, and do it *without* the loading state — the form is
     *    replaced by a newer version of itself, which is the whole promise of a live client.
     *    A spinner over an unchanged screen would be motion reporting nothing.
     *  - **Something typed.** **Do not touch the fields.** The window says so instead, through
     *    [State.Ready.hasChangedElsewhere], and the person decides: save over it, or reload and
     *    lose what they wrote. Silently overwriting the field under a cursor is the one outcome
     *    no amount of liveness justifies.
     *  - **Deleted.** The reload finds a 404 and lands on [State.Gone], which is the state that
     *    already exists for exactly this — see [WindowContent.EditBookmark]. It is reached from
     *    the first branch, because a window nobody has typed into is one that can afford to
     *    find out.
     *
     *    **A window with unsaved changes is not moved to `Gone` by this event**, which is the
     *    corollary of the second branch and is deliberate: it goes there on the next save
     *    instead, carrying the server's own 404. Telling somebody their bookmark is gone *and*
     *    throwing away what they had typed about it, in one unasked-for repaint, is two losses
     *    where the ticket asks for none.
     *
     * A no-op for a window that is already `Gone` or `Failed`: neither has anything to lose and
     * neither is showing something this could correct — [onReloadRequested] is the way back from
     * both, and it is a gesture rather than a background event.
     */
    fun onChangedElsewhere() {
        val ready = _stateFlow.value.ready ?: return
        if (ready.hasUnsavedChanges || ready.isSaving) {
            _stateFlow.value = State(ready = ready.copy(hasChangedElsewhere = true))
            return
        }
        // No loading state, deliberately — see above. `reloads` is what `run` collects, and
        // `load()` replaces the state whole when its answer arrives.
        reloads.value += 1
    }

    /**
     * Fetch the bookmark and the account's tags, and turn whatever happens into a state.
     *
     * The tag vocabulary is fetched here rather than by the field that draws it, because a
     * `#tag` menu with no counts in it is a different control (see
     * [se.soderbjorn.lunapin.clientserver.TagInputRules]) and every platform's editor wants
     * the same one. Its failure is **not** the window's failure, though: a tag list that
     * did not arrive costs the menu its rows, and refusing to show a form somebody asked
     * for because the autocomplete is unavailable would be the wrong trade by a distance.
     */
    private suspend fun load() {
        _stateFlow.value = try {
            val bookmark = storage.bookmark(bookmarkId)
            if (bookmark == null) {
                // Not a failure. This account has no such bookmark, which is exactly what
                // the window has to say — see [State.Gone].
                State(gone = State.Gone())
            } else {
                State(ready = readyFor(bookmark, knownTags = knownTags()))
            }
        } catch (cancellation: CancellationException) {
            // Not a failure to report: the window closed, or the scope went with it.
            throw cancellation
        } catch (failure: Exception) {
            State(failed = State.Failed(failure.readableMessage(COULD_NOT_LOAD)))
        }
    }

    /** The account's tags, or none — see [load] on why this failure is survivable. */
    private suspend fun knownTags(): List<TagUse> =
        try {
            storage.tags()
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (_: Exception) {
            emptyList()
        }

    // ── Editing ──────────────────────────────────────────────────────────────
    //
    // Every intent below is a no-op unless the window is in [State.Ready] and not
    // mid-save. That is one guard, written once in [editing], rather than a null check per
    // intent — and it is what makes "the form is frozen while it is being written" a fact
    // about the model rather than something each platform has to remember to enforce by
    // disabling controls.

    /** The user typed in the title field. Blank is a real value here; see [saveRequest]. */
    fun onTitleEdited(title: String) {
        editing { it.copy(title = title, message = null) }
    }

    /** The user typed in the note field. This is the *user's* note, never the model's. */
    fun onNoteEdited(note: String) {
        editing { it.copy(note = note, message = null) }
    }

    /**
     * The user committed a tag in the `#tag` field.
     *
     * Normalised the way the server will normalise it — deduplicated and sorted — so the
     * chips do not rearrange themselves the moment a save comes back. The server is still
     * the authority; this only keeps the two from disagreeing for a second.
     */
    fun onTagAdded(tag: String) {
        val clean = tag.trim().lowercase()
        if (clean.isEmpty()) return
        editing { ready ->
            if (clean in ready.tags) ready else ready.copy(tags = (ready.tags + clean).sorted())
        }
    }

    /** The user pressed a chip's `×`. Removing the last one is allowed, and clears them. */
    fun onTagRemoved(tag: String) {
        editing { it.copy(tags = it.tags - tag) }
    }

    // ── Saving ───────────────────────────────────────────────────────────────

    /**
     * The user pressed Save, or `⌘⏎`.
     *
     * Three outcomes, and the third is the one this window exists to get right:
     *
     *  - the row comes back and becomes the new baseline;
     *  - the server refuses with a sentence — a URL it will not store, a link already
     *    saved elsewhere — which is shown *over the form the user is still holding*,
     *    because they have to be able to fix it;
     *  - **404**, meaning this account no longer has this bookmark. The window goes to
     *    [State.Gone] rather than falling back to a `POST`, which would recreate a row
     *    somebody deliberately deleted and hand back a *different* id under the same
     *    window. A save cannot resurrect.
     */
    fun onSaveRequested() {
        val ready = editable() ?: return
        publish(ready.copy(isSaving = true, isConfirmingDelete = false, message = null))
        scope.launch {
            try {
                val saved = storage.editBookmark(bookmarkId, saveRequest(ready))
                // The server's answer, not the request: it trims, normalises tags and
                // keeps its own clock, so echoing the request back would leave the form
                // describing something slightly other than what is stored.
                publish(readyFor(saved, knownTags = ready.knownTags))
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (failure: Exception) {
                if (failure.isGone()) {
                    _stateFlow.value = State(gone = State.Gone())
                } else {
                    publish(
                        ready.copy(
                            isSaving = false,
                            message = failure.readableMessage(COULD_NOT_SAVE),
                        ),
                    )
                }
            }
        }
    }

    /**
     * The request one Save sends, and the argument for its least obvious field.
     *
     * **[EditBookmarkRequest.url] is the stored one, verbatim.** The window does not offer
     * to change it (see the file header), and sending back what came down is what makes
     * that true on the wire rather than only in the layout.
     *
     * Everything else is sent exactly as typed, blanks included. That is the whole reason
     * this route exists: an empty title clears the title and an empty list removes every
     * tag, because the person looking at the form emptied it on purpose.
     */
    private fun saveRequest(ready: State.Ready): EditBookmarkRequest = EditBookmarkRequest(
        url = ready.bookmark.url,
        title = ready.title,
        tags = ready.tags,
        summary = ready.note,
    )

    // ── Asking for the summary again ─────────────────────────────────────────

    /**
     * The user pressed *Regenerate*.
     *
     * Read the page again, summarise it and tag it. The answer for a summary that came back
     * wrong, a page that was behind a login the first time, and a page that has been
     * rewritten since — three problems with one answer, which is why there is one control.
     *
     * ── It updates the baseline and leaves the form alone ────────────────────
     *
     * Only [State.Ready.bookmark] is replaced, not the whole state. The person may have
     * typed a title or half a note before pressing this, and rebuilding the form from the
     * server's answer would throw that away — for a call that changed nothing they had
     * typed. What it did change is the right-hand column, which is the system's half: the
     * state becomes `pending`, the stale failure sentence is gone, and the panel says a
     * summary is on its way.
     *
     * **[State.Ready.isSaving] is not set.** The form is not being written and nothing the
     * user types can be lost, so freezing it would be freezing a form for somebody else's
     * request. The button says what is happening instead — see
     * [State.Ready.isAwaitingSummary].
     *
     * A refusal — no key on this deployment, or both AI switches off for this account —
     * arrives as the server's own sentence over the form, which is where every other
     * refusal on this window arrives and is what those routes refuse *for*. This window
     * deliberately does not hide the control on an account that has AI off: it does not
     * fetch account settings, and a button that explains itself when pressed is better than
     * one that silently is not there.
     *
     * A 404 is the *gone* state, exactly as a save's is and for the same reason: this
     * account no longer has this bookmark, and there is nothing here that could bring it
     * back.
     */
    fun onRegenerateRequested() {
        val ready = editable() ?: return
        if (ready.bookmark.isAwaitingSummary) return
        publish(ready.copy(message = null))
        scope.launch {
            try {
                val queued = storage.summariseBookmark(bookmarkId)
                // The server's answer for the baseline, the user's own typing untouched.
                _stateFlow.value.ready?.let { publish(it.copy(bookmark = queued)) }
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (failure: Exception) {
                if (failure.isGone()) {
                    _stateFlow.value = State(gone = State.Gone())
                } else {
                    _stateFlow.value.ready?.let {
                        publish(it.copy(message = failure.readableMessage(COULD_NOT_REGENERATE)))
                    }
                }
            }
        }
    }

    // ── Deleting ─────────────────────────────────────────────────────────────

    /**
     * The user pressed Delete. Asks first.
     *
     * The confirmation lives in the state rather than in a dialog each platform builds,
     * because *whether it is confirmed* is the rule and a dialog is one way of asking. A
     * window can afford to ask in place — the list, which has no delete at all today, is
     * where a modal would have been the only option.
     */
    fun onDeleteRequested() {
        editing { it.copy(isConfirmingDelete = true, message = null) }
    }

    /** The user backed out of the confirmation. */
    fun onDeleteCancelled() {
        editing { it.copy(isConfirmingDelete = false) }
    }

    /**
     * The user confirmed the delete.
     *
     * A 404 here is success rather than a failure: it means the row was already gone, and
     * "the bookmark you asked me to delete does not exist" is what the user wanted. The
     * two land in the same state and differ only in [State.Gone.deletedHere], which is how
     * a platform knows whether to close the window (you asked) or show the *gone* panel
     * (something else happened).
     */
    fun onDeleteConfirmed() {
        val ready = editable() ?: return
        if (!ready.isConfirmingDelete) return
        publish(ready.copy(isSaving = true))
        scope.launch {
            try {
                storage.deleteBookmark(bookmarkId)
                _stateFlow.value = State(gone = State.Gone(deletedHere = true))
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (failure: Exception) {
                if (failure.isGone()) {
                    _stateFlow.value = State(gone = State.Gone(deletedHere = true))
                } else {
                    publish(
                        ready.copy(
                            isSaving = false,
                            isConfirmingDelete = false,
                            message = failure.readableMessage(COULD_NOT_DELETE),
                        ),
                    )
                }
            }
        }
    }

    // ── Internals ────────────────────────────────────────────────────────────

    /** A fresh [State.Ready] over what the server holds — the form and its baseline. */
    private fun readyFor(bookmark: Bookmark, knownTags: List<TagUse>) = State.Ready(
        bookmark = bookmark,
        title = bookmark.title,
        tags = bookmark.tags,
        note = bookmark.summary,
        knownTags = knownTags,
    )

    /** The form, if there is one and it is not mid-write. */
    private fun editable(): State.Ready? = _stateFlow.value.ready?.takeIf { !it.isSaving }

    /** Apply [transform] to the form, or do nothing if there is no editable one. */
    private inline fun editing(transform: (State.Ready) -> State.Ready) {
        val ready = editable() ?: return
        publish(transform(ready))
    }

    /**
     * Publish a form state, unless the window has left the form behind.
     *
     * The guard is what a delete confirmed while a save is in flight needs: the write
     * completing afterwards must not put the form back over a window that has since gone
     * to [State.Gone].
     */
    private fun publish(ready: State.Ready) {
        if (_stateFlow.value.gone != null) return
        _stateFlow.value = State(ready = ready)
    }

    /**
     * Whether this failure is the server saying it has no such bookmark.
     *
     * One predicate for both routes, because both answer it the same way and for the same
     * reason: `404` covers "no such row" and "not yours" identically, so this side cannot
     * tell them apart and does not try. Anything else — a 400 the store wrote a sentence
     * for, a 401, a transport failure — is a message over the form rather than a verdict
     * about the bookmark's existence.
     */
    private fun Exception.isGone(): Boolean =
        (this as? ApiFailure)?.status == HttpStatusCode.NotFound

    /**
     * Exactly one field is non-null.
     *
     * The nullable-payload shape from §06 rather than a sealed hierarchy, so a Swift view
     * reads it as an `if let` chain rather than a cast per branch.
     *
     * @property loading the bookmark is being fetched. The first state of every window.
     * @property ready the form, over a bookmark that exists.
     * @property gone this account has no such bookmark. **A state, not an error** — see
     *   [Gone].
     * @property failed the bookmark could not be fetched, with a sentence to show.
     *   Distinct from [gone]: "we could not ask" is not "it is not there", and a window
     *   that confused the two would tell somebody their bookmark had been deleted every
     *   time their network dropped.
     */
    data class State(
        val loading: Loading? = null,
        val ready: Ready? = null,
        val gone: Gone? = null,
        val failed: Failed? = null,
    ) {
        data object Loading

        /**
         * The form, and the bookmark it is a form over.
         *
         * **[isSaving] is a flag here rather than a sixth state of its own**, and that is
         * the shape rather than a shortcut: a save leaves the form on screen — the user is
         * still looking at what they typed, and a refusal has to arrive over it — so a
         * top-level `Saving` payload would have to carry every field of this one in order
         * to render at all. What it buys instead is one rule: while [isSaving] is true no
         * intent changes anything, so nothing can be typed into a form that is mid-flight
         * and lost when the answer lands.
         *
         * @property bookmark what the server holds, as of the last answer. The baseline
         *   the fields started from and the source of everything the window shows but does
         *   not edit — the URL, when it was added, whether it has been read, the AI
         *   summary and state, and which tags the model added.
         * @property title as typed. **Blank is a real value**: this route can clear a
         *   title, which is the whole reason it exists.
         * @property tags as committed, `#`-prefixed and lower-cased, deduplicated and
         *   sorted. Empty is a real value and clears every tag.
         *
         *   Which of them the model added is [Bookmark.aiTags] on [bookmark], not a second
         *   list here — a chip carries one small `AI` mark and is otherwise an ordinary
         *   tag, because AI tags are applied rather than proposed.
         * @property note the user's own note — [Bookmark.summary], never [Bookmark.aiSummary].
         *   Blank clears it.
         * @property knownTags the account's vocabulary with counts, for the `#tag` menu.
         *   Empty when the tag route did not answer, which costs the menu its rows and
         *   nothing else.
         * @property isSaving a write is in flight. See above.
         * @property isConfirmingDelete Delete has been pressed and is waiting to be
         *   confirmed.
         * @property message the server's own sentence about the last write, or null. Shown
         *   over the form rather than in place of it, because the thing it is about is
         *   still on screen and still fixable.
         * @property hasChangedElsewhere this bookmark was changed somewhere else while there
         *   was unsaved work here, so the form is **deliberately stale** and says so.
         *
         *   Set only in that case — see [EditBookmarkBackingViewModel.onChangedElsewhere],
         *   where the three branches are argued. A window with nothing typed simply reloads
         *   and this stays false, because there is nothing to warn about when the newer
         *   version has already replaced the older one.
         *
         *   Cleared by a save (which republishes from the server's answer) and by a reload.
         *   Not cleared by typing: what it reports is a fact about the *server*, and typing
         *   another character does not make the other client's change go away.
         */
        data class Ready(
            val bookmark: Bookmark,
            val title: String,
            val tags: List<String>,
            val note: String,
            val knownTags: List<TagUse> = emptyList(),
            val isSaving: Boolean = false,
            val isConfirmingDelete: Boolean = false,
            val message: String? = null,
            val hasChangedElsewhere: Boolean = false,
        ) {
            /** The link itself. Shown and copied, never edited — see the file header. */
            val url: String get() = bookmark.url

            /**
             * Whether a summary is on its way — freshly asked for, or still queued from the
             * save that created this bookmark.
             *
             * What *Regenerate* reads to disable itself and to say *Summarising…* instead:
             * pressing it again while the queue already holds this bookmark would reset its
             * attempt count and its place in line to no purpose.
             */
            val isAwaitingSummary: Boolean get() = bookmark.isAwaitingSummary

            /**
             * Whether the three editable fields still say what the server said.
             *
             * The question a platform has to answer before it closes this window, and the
             * reason it is here rather than in the surface that asks it: "has anything been
             * typed" is a fact about the form, every platform needs the same answer, and a
             * web version and an iOS version of the same comparison would drift the first
             * time a fourth field appears.
             *
             * It is a comparison against [bookmark] rather than a flag that intents set,
             * which is what makes typing a character and deleting it again *clean*. The
             * baseline is the server's own answer — [readyFor] seeds the form from it, and
             * a save republishes both from what came back — so the three fields and the
             * three things they are compared to are never two different normalisations of
             * the same value.
             *
             * The system's half of the window is deliberately not in it: *Regenerate*
             * replaces [bookmark] and leaves the form alone, and a summary arriving is not
             * something a person can lose by closing a window.
             *
             * **The tags are compared sorted**, and that is not tidiness. Committing one
             * sorts the whole list — see [onTagAdded], which does it so the chips do not
             * rearrange themselves when a save comes back — while the stored list is in
             * whatever order the server sent. So adding a tag and taking it straight back
             * off can leave the same tags in a different order, and a plain list
             * comparison would call that unsaved work and raise a dialog about a change
             * the user had already undone.
             */
            val hasUnsavedChanges: Boolean
                get() = title != bookmark.title ||
                    note != bookmark.summary ||
                    tags.sorted() != bookmark.tags.sorted()
        }

        /**
         * There is no such bookmark, so there is nothing to edit.
         *
         * The state the ticket is really about. A bookmark can be deleted from another
         * window, from a phone, or from the bookmarklet's popup while this window sits
         * open on it, and the alternatives to saying so are both bad: a Save that quietly
         * `POST`s the row back recreates something somebody deleted on purpose, and a
         * generic failure sends them looking for a network problem that is not there.
         *
         * @property deletedHere whether this window is what deleted it. The platform reads
         *   it to decide between closing the window — the user asked for this, and a panel
         *   explaining what they just did is furniture — and showing the panel, which is
         *   the only way anybody learns that the row went somewhere else.
         */
        data class Gone(val deletedHere: Boolean = false)

        data class Failed(val message: String)
    }

    private companion object {
        /** Only reached when the failure carried no sentence of its own. */
        const val COULD_NOT_LOAD = "Could not load this bookmark."
        const val COULD_NOT_SAVE = "Could not save this bookmark."
        const val COULD_NOT_DELETE = "Could not delete this bookmark."
        const val COULD_NOT_REGENERATE = "Could not ask for a summary."
    }
}
